import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, DialogContent, IconButton, Link } from '@mui/material';
import {
  DESIGNBUILDER_SUPPORT_EMAIL,
  idfErrorDialogTextSx,
  labPrimaryBtnClass,
  labSecondaryBtnClass,
} from 'components/IdfParseResultStep';
import { useTranslation } from 'react-i18next';

type V2EnergyPlusNoticeDialogProps = {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
  onNewBuilding: () => void;
};

/**
 * Shown when creating a revision on a v2 project.
 * Same chrome as the IDF labs parser result dialog.
 */
const V2EnergyPlusNoticeDialog = ({
  open,
  onClose,
  onContinue,
  onNewBuilding,
}: V2EnergyPlusNoticeDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      className="v2-energyplus-notice-dialog"
      sx={{
        '& .MuiDialog-paper': {
          position: 'relative',
          borderRadius: '8px',
          width: 'max-content',
          maxWidth: 'calc(100vw - 32px)',
          m: 2,
          overflow: 'visible',
        },
      }}
    >
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 8,
          insetInlineEnd: 8,
          zIndex: 1,
          color: 'grey.500',
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        sx={{
          px: 3,
          pt: 5,
          pb: 3,
          overflow: 'visible',
          width: 'max-content',
          maxWidth: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 1.5,
            width: 'max-content',
            maxWidth: '100%',
            '& a': {
              fontSize: 'inherit',
              fontWeight: 'inherit',
              lineHeight: 'inherit',
              verticalAlign: 'baseline',
            },
          }}
        >
          <Box
            component="p"
            sx={{ ...idfErrorDialogTextSx, fontWeight: 700, fontSize: '18px' }}
          >
            {t('V2_EP_NOTICE_TITLE')}
          </Box>
          <Box
            component="p"
            sx={{ ...idfErrorDialogTextSx, fontWeight: 700, fontSize: '18px' }}
          >
            {t('V2_EP_NOTICE_BODY')}
          </Box>

          <Box component="p" sx={{ ...idfErrorDialogTextSx, fontSize: '16px' }}>
            <Link href="/how-it-works" target="_blank" underline="always">
              {t('V2_EP_NOTICE_HOW')}
            </Link>
          </Box>

          <Box
            component="p"
            sx={{
              ...idfErrorDialogTextSx,
              fontSize: '16px',
              whiteSpace: 'nowrap',
            }}
          >
            {t('V2_EP_NOTICE_SUPPORT')}{' '}
            <Link
              href={`mailto:${DESIGNBUILDER_SUPPORT_EMAIL}`}
              underline="always"
            >
              {DESIGNBUILDER_SUPPORT_EMAIL}
            </Link>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
              width: '100%',
              pt: 1,
            }}
          >
            <button
              type="button"
              className={labPrimaryBtnClass}
              style={{ width: 'auto', flex: '1 1 240px' }}
              onClick={onNewBuilding}
            >
              <span aria-hidden="true" style={{ fontSize: 28, lineHeight: 1 }}>
                +
              </span>
              {t('PROJECTS_ADD_BUTTON')}
            </button>
            <button
              type="button"
              className={labSecondaryBtnClass}
              style={{ width: 'auto', flex: '1 1 240px' }}
              onClick={onContinue}
            >
              {t('V2_EP_NOTICE_CONTINUE')}
            </button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default V2EnergyPlusNoticeDialog;
